import type { ExchangeRate, MusicLicenseCost, MusicRevenue } from '@qlk/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  type CotBang,
  DataTable,
  EmptyState,
  KhoiMuc,
  Money,
  ngayDay,
  Nut,
  PageHeader,
  soNguyen,
  ToolbarBang,
} from '../../../ui/index.js';
import { O, OChonTruong } from '../../../ui/index.js';
import { usePhamViDuLieu } from '../../../lib/auth.js';
import { cacKyGanDay, kyHienTai, tenKy } from '../../../lib/ky.js';
import {
  useDoanhThuNhac,
  useLuuPhiNhac,
  useLuuTyGia,
  useNguonNhac,
  usePhiNhac,
  useThemDoanhThuNhac,
  useTyGia,
} from '../api.js';

/** Trang nhập doanh thu nhạc Content ID, phí kho nhạc hợp tác và tỷ giá. */
export function TrangNhapDoanhThu() {
  const [period, datPeriod] = useState(kyHienTai());
  const { biGioiHanKenh } = usePhamViDuLieu();

  const dauTrang = (
    <PageHeader
      breadcrumb={['TÀI CHÍNH', 'Doanh thu']}
      title="Nhập doanh thu nhạc, phí giấy phép và tỷ giá"
      phu={biGioiHanKenh ? 'Chỉ quản trị xem' : `Kỳ đang nhập: ${tenKy(period)}`}
      giaiThich="Số liệu nhập tay, không gọi API bên ngoài. Tỷ giá dùng để quy đổi VND khi xuất Excel."
      actions={
        <Link to="/doanh-thu" className="text-co-ban font-semibold text-chu-phu hover:text-chu">
          ← Doanh thu
        </Link>
      }
    />
  );

  /*
   * Cả ba khối của trang này đều là khoản **của cả công ty** (Content ID kho
   * nhạc, phí kho nhạc hợp tác, tỷ giá USD→VND) nên máy chủ trả 403 cho người
   * bị giới hạn theo kênh được giao — mục V4a. Nút vào đây đã ẩn ở trang Doanh
   * thu, nhưng đường dẫn vẫn gõ tay được, và route chỉ đòi `revenue:edit`.
   *
   * Nói một câu đúng, thay cho ba khung đỏ "Không tải được…".
   */
  if (biGioiHanKenh) {
    return (
      <>
        {dauTrang}
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            title="Mục này chỉ quản trị xem"
            description="Content ID kho nhạc, phí kho nhạc hợp tác và tỷ giá USD→VND không gắn kênh nào — chúng là khoản thu/chi của cả công ty. Vai trò của bạn chỉ xem số liệu các kênh được giao."
            action={<Link to="/doanh-thu">← Về trang Doanh thu</Link>}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {dauTrang}

      {/*
        Một ô lọc duy nhất: `ToolbarBang` thay cho cái thẻ viền-nền-padding-16
        chỉ để bọc một `<select>` — thẻ đó cao 72px mà không nói gì thêm so với
        chính ô chọn.
      */}
      <ToolbarBang
        trai={
          <OChonTruong
            label="Kỳ đang nhập"
            value={period}
            onChange={datPeriod}
            options={cacKyGanDay(12).map((p) => ({ value: p, label: tenKy(p) }))}
            className="min-w-[200px]"
          />
        }
      />

      <div className="grid grid-cols-1 gap-5 @min-[900px]/noi-dung:grid-cols-2">
        <KhoiNhacContentId period={period} />
        <KhoiPhiGiayPhep period={period} />
      </div>

      <div className="mt-5">
        <KhoiTyGia />
      </div>
    </>
  );
}

/* ----------------------- Doanh thu Content ID ------------------------ */

function KhoiNhacContentId({ period }: { period: string }) {
  const ds = useDoanhThuNhac(period);
  const them = useThemDoanhThuNhac();

  const [form, datForm] = useState({ source: '', amount: '', note: '' });

  const cot: CotBang<MusicRevenue>[] = [
    { key: 'nguon', header: 'Nguồn', render: (n) => n.source },
    { key: 'soTien', header: 'Số tiền', align: 'right', render: (n) => <Money value={n.amount} /> },
    { key: 'ghiChu', header: 'Ghi chú', render: (n) => n.note ?? '—' },
  ];

  return (
    <KhoiMuc
      tieuDe="Content ID nhạc Miss Records"
      moTa="Tiền nhận từ kênh bên ngoài dùng nhạc của công ty, nhập theo báo cáo nhà phân phối"
    >
      <form
        className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          them.mutate(
            {
              period,
              source: form.source.trim(),
              amount: Number(form.amount),
              currency: 'USD',
              note: form.note.trim() || null,
            },
            {
              onSuccess: () => {
                datForm({ source: '', amount: '', note: '' });
              },
            },
          );
        }}
      >
        <O
          label="Nhà phân phối"
          value={form.source}
          onChange={(v) => {
            datForm({ ...form, source: v });
          }}
          required
        />
        <O
          label="Số tiền (USD)"
          type="number"
          step={0.01}
          value={form.amount}
          onChange={(v) => {
            datForm({ ...form, amount: v });
          }}
          required
        />
        <O
          label="Ghi chú"
          value={form.note}
          onChange={(v) => {
            datForm({ ...form, note: v });
          }}
          className="sm:col-span-2"
        />
        <div className="sm:col-span-2">
          <Nut kieu="chinh" type="submit" dangChay={them.isPending}>
            Thêm khoản thu
          </Nut>
          {them.error && <p className="mt-2 text-phu text-bad-chu">{them.error.message}</p>}
        </div>
      </form>

      <DataTable
        caption="Doanh thu Content ID của nhạc trong kỳ"
        columns={cot}
        rows={ds.data ?? []}
        keyOf={(n) => n.id}
        dangTai={ds.isPending}
        khiRong={<EmptyState title="Kỳ này chưa nhập khoản nào" />}
      />
    </KhoiMuc>
  );
}

/* ------------------------- Phí kho nhạc ------------------------------ */

function KhoiPhiGiayPhep({ period }: { period: string }) {
  const ds = usePhiNhac(period);
  const nguon = useNguonNhac();
  const luu = useLuuPhiNhac();

  const [form, datForm] = useState({ musicSourceId: '', amount: '', note: '' });

  const cot: CotBang<MusicLicenseCost>[] = [
    { key: 'nguon', header: 'Kho nhạc', render: (p) => p.musicSourceName },
    { key: 'soTien', header: 'Phí', align: 'right', render: (p) => <Money value={p.amount} /> },
    { key: 'ghiChu', header: 'Ghi chú', render: (p) => p.note ?? '—' },
  ];

  const nguonHopTac = (nguon.data ?? []).filter((n) => n.kind !== 'own');

  return (
    <KhoiMuc
      tieuDe="Phí kho nhạc hợp tác"
      moTa="Kỳ chưa nhập thì màn hình Doanh thu ghi “Theo hợp đồng”"
    >
      <form
        className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.musicSourceId) return;
          luu.mutate(
            {
              period,
              musicSourceId: form.musicSourceId,
              amount: Number(form.amount || 0),
              currency: 'USD',
              note: form.note.trim() || null,
            },
            {
              onSuccess: () => {
                datForm({ musicSourceId: '', amount: '', note: '' });
              },
            },
          );
        }}
      >
        <OChonTruong
          label="Kho nhạc"
          value={form.musicSourceId}
          onChange={(v) => {
            datForm({ ...form, musicSourceId: v });
          }}
          options={[
            { value: '', label: 'Chọn kho nhạc…' },
            ...nguonHopTac.map((n) => ({ value: n.id, label: n.name })),
          ]}
        />
        <O
          label="Phí (USD)"
          type="number"
          step={0.01}
          min={0}
          value={form.amount}
          onChange={(v) => {
            datForm({ ...form, amount: v });
          }}
          required
        />
        <O
          label="Ghi chú"
          value={form.note}
          onChange={(v) => {
            datForm({ ...form, note: v });
          }}
          className="sm:col-span-2"
        />
        <div className="sm:col-span-2">
          <Nut kieu="chinh" type="submit" dangChay={luu.isPending} disabled={!form.musicSourceId}>
            Lưu phí
          </Nut>
          {luu.error && <p className="mt-2 text-phu text-bad-chu">{luu.error.message}</p>}
          {nguon.error && (
            <p className="mt-2 text-phu text-warn-chu">
              Chưa lấy được danh sách kho nhạc: {nguon.error.message}
            </p>
          )}
        </div>
      </form>

      <DataTable
        caption="Phí kho nhạc hợp tác trong kỳ"
        columns={cot}
        rows={ds.data ?? []}
        keyOf={(p) => p.id}
        dangTai={ds.isPending}
        khiRong={<EmptyState title="Kỳ này chưa nhập phí nào" />}
      />
    </KhoiMuc>
  );
}

/* ------------------------------ Tỷ giá ------------------------------- */

function KhoiTyGia() {
  const ds = useTyGia();
  const luu = useLuuTyGia();

  const [form, datForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    rate: '',
  });

  const cot: CotBang<ExchangeRate>[] = [
    {
      key: 'ngay',
      header: 'Ngày áp dụng',
      render: (t) => <span className="so-lieu">{ngayDay(t.date)}</span>,
    },
    { key: 'cap', header: 'Cặp tiền', render: (t) => `${t.from} → ${t.to}` },
    {
      key: 'tyGia',
      header: 'Tỷ giá',
      align: 'right',
      render: (t) => <span className="so-lieu">{soNguyen(Number(t.rate))}</span>,
    },
  ];

  return (
    <KhoiMuc
      tieuDe="Tỷ giá USD → VND"
      moTa="Nhập tay; kỳ lấy tỷ giá gần nhất không muộn hơn ngày cuối kỳ"
    >
      <form
        className="mb-5 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          luu.mutate(
            {
              date: new Date(`${form.date}T00:00:00.000Z`).toISOString(),
              from: 'USD',
              to: 'VND',
              rate: Number(form.rate),
            },
            {
              onSuccess: () => {
                datForm({ ...form, rate: '' });
              },
            },
          );
        }}
      >
        <O
          label="Ngày áp dụng"
          type="date"
          value={form.date}
          onChange={(v) => {
            datForm({ ...form, date: v });
          }}
          required
          className="w-[200px]"
        />
        <O
          label="1 USD = ? VND"
          type="number"
          step={1}
          min={1}
          value={form.rate}
          onChange={(v) => {
            datForm({ ...form, rate: v });
          }}
          required
          className="w-[200px]"
        />
        <Nut kieu="chinh" type="submit" dangChay={luu.isPending}>
          Lưu tỷ giá
        </Nut>
        {luu.error && <p className="text-phu text-bad-chu">{luu.error.message}</p>}
      </form>

      <DataTable
        caption="Lịch sử tỷ giá đã nhập"
        columns={cot}
        rows={ds.data ?? []}
        keyOf={(t) => t.id}
        dangTai={ds.isPending}
        khiRong={<EmptyState title="Chưa nhập tỷ giá nào" />}
      />
    </KhoiMuc>
  );
}
